import { useTranslation } from 'react-i18next';
import React, { useEffect, useRef } from 'react';
import { FormControl, FormHelperText, RadioGroup } from '@mui/material';

import { IUnregisteredStepFive } from '../unregistered-steps-types';
import { FaultType } from '../../../../../models/assistance.model';
import { FormLayout } from '../../../../../global-styles/layout';
import { getErrorMessage } from '../../../../../constants/errorMessages';
import { DESPLEGAR_NO_ARRANCA, MOSTRAR_NO_ARRANCA } from '../../../../../constants/env';
import { BodySmallText, Heading4Text } from '../../../../../components/typography/typography';
import Radio from '../../../../../components/radio/radio';
import RadioCard from '../../../../../components/common/radio-card/radio-card';
import ControlledInput from '../../../../../components/common/forms/ControlledInput';
import ControlledCounter from '../../../../../components/common/forms/ControlledCounter';
import CardSubsection from '../../../../../components/card-subsection/card-subsection';
import { UnregisteredStep5Style } from './unregistered-5-style';

const Unregistered5: React.FC<IUnregisteredStepFive> = ({ form }) => {
  const { t } = useTranslation(['common', 'auth']);

  const {
    control,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    formState: { errors, dirtyFields },
    watch,
    setValue,
    register,
    unregister,
  } = form;
  const [faultType, notStartingReason, tyreChange, blockedTyres, faultInformation] = watch([
    'faultType',
    'notStartingReason',
    'tyreChange',
    'blockedTyres',
    'faultInformation',
  ]);

  const prevValue = useRef<FaultType>(faultType);

  useEffect(() => {
    if (faultType !== FaultType.notStarting) {
      unregister('notStartingReason');
    } else if (!DESPLEGAR_NO_ARRANCA) {
      setValue('notStartingReason', 0);
    }

    if (faultType !== FaultType.puncture) {
      unregister(['affectedTyres', 'tyreChange']);
    }

    if (faultType !== FaultType.other) {
      unregister('otherFaultInformation');
    }

    if (!blockedTyres) {
      unregister('outOfHighway');
    }

    if (faultType !== FaultType.battery && faultType !== FaultType.notStarting) {
      unregister('redBatteryLight');
    }

    if (faultType !== prevValue.current) {
      setValue('redBatteryLight', null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tyreChange, blockedTyres, faultType]);

  useEffect(() => {
    prevValue.current = faultType;
  }, [faultType]);

  // Registro único de faultType con validación
  useEffect(() => {
    register('faultType', {
      required: 'Este campo es requerido',
      validate: {
        validFaultType: (value) =>
          Object.values(FaultType).includes(value) || 'Debe seleccionar un tipo de fallo válido',
      },
    });
  }, [register]);

  return (
    <UnregisteredStep5Style>
      {/* ¿Qué le sucede al vehículo? */}
      <Heading4Text className="m-12px" weight={600}>
        {t('auth:unregistered.step-4.title-2')}
      </Heading4Text>
      <BodySmallText className="m-24px"> {t('auth:unregistered.step-4.text-2')}</BodySmallText>

      <FormControl component="fieldset">
        <FormLayout className="m-24px">
          {MOSTRAR_NO_ARRANCA && (
            <RadioCard
              id={'faultType-1'}
              dataCy={FaultType.notStarting.toString()}
              selectedValue={faultType === FaultType.notStarting}
              data={{
                value: FaultType.notStarting,
                title: t('auth:unregistered.step-4.no-start'),
                icon: 'far:car-bolt',
              }}
              onClick={() => {
                setValue('faultType', FaultType.notStarting, { shouldValidate: true, shouldDirty: true });
                if (!DESPLEGAR_NO_ARRANCA) {
                  // focus se gestiona en el paso de pasajeros
                }
              }}
            />
          )}
          {faultType === FaultType.notStarting && MOSTRAR_NO_ARRANCA && DESPLEGAR_NO_ARRANCA && (
            <CardSubsection>
              <FormLayout>
                <RadioGroup aria-label="notStartingReason">
                  <Radio
                    id={'notStartingReason-1'}
                    dataCy={'notStartingReason-1'}
                    active={notStartingReason === 0}
                    label={t('auth:unregistered.step-4.not-start-stop')}
                    value="0"
                    className="mb-15px"
                    onClick={() => {
                      setValue('notStartingReason', 0);
                    }}
                  />
                  <Radio
                    id={'notStartingReason-2'}
                    dataCy={'notStartingReason-2'}
                    active={notStartingReason === 1}
                    label={t('auth:unregistered.step-4.not-start-others')}
                    value="1"
                    className="mb-15px"
                    onClick={() => {
                      setValue('notStartingReason', 1);
                    }}
                  />
                </RadioGroup>
              </FormLayout>
            </CardSubsection>
          )}
          <RadioCard
            id={'faultType-2'}
            selectedValue={faultType === FaultType.battery}
            dataCy={FaultType.battery.toString()}
            data={{
              value: FaultType.battery,
              title: t('auth:unregistered.step-4.no-batery'),
              icon: 'far:car-battery',
            }}
            onClick={() => {
              setValue('faultType', FaultType.battery, { shouldValidate: true, shouldDirty: true });
            }}
          />
          <RadioCard
            id={'faultType-3'}
            selectedValue={faultType === FaultType.gas}
            dataCy={FaultType.gas.toString()}
            data={{
              value: FaultType.gas,
              title: t('auth:unregistered.step-4.no-fuel'),
              icon: 'far:gas-pump',
            }}
            onClick={() => {
              setValue('faultType', FaultType.gas, { shouldValidate: true, shouldDirty: true });
            }}
          />
          <RadioCard
            id={'faultType-4'}
            selectedValue={faultType === FaultType.keys}
            dataCy={FaultType.keys.toString()}
            data={{
              value: FaultType.keys,
              title: t('auth:unregistered.step-4.keys'),
              icon: 'far:car-key',
            }}
            onClick={() => {
              setValue('faultType', FaultType.keys, { shouldValidate: true, shouldDirty: true });
            }}
          />
          <RadioCard
            id={'faultType-5'}
            selectedValue={faultType === FaultType.puncture}
            dataCy={FaultType.puncture.toString()}
            data={{
              value: FaultType.puncture,
              title: t('auth:unregistered.step-4.wheels'),
              icon: 'fal:tire',
            }}
            onClick={() => {
              setValue('faultType', FaultType.puncture, { shouldValidate: true, shouldDirty: true });
            }}
          />
          {faultType === FaultType.puncture && (
            <CardSubsection>
              <FormLayout>
                <ControlledCounter
                  counterProps={{
                    label: t('auth:unregistered.step-4.affected-tyres'),
                    maxCount: 4,
                    minCount: 0,
                    dataCy: 'affectedTyres',
                  }}
                  controllerProps={{
                    name: 'affectedTyres',
                    control,
                    rules: {
                      validate: {
                        isValidTyreNumber: (tyres: number) => tyres > 0,
                      },
                    },
                  }}
                />

                <FormHelperText className="error-block" error>
                  {getErrorMessage(t, errors.affectedTyres?.type)}
                </FormHelperText>
              </FormLayout>
            </CardSubsection>
          )}
          <RadioCard
            id={'faultType-6'}
            selectedValue={faultType === FaultType.other}
            dataCy={FaultType.other.toString()}
            data={{
              value: FaultType.other,
              title: t('auth:unregistered.step-4.other-breakdowns'),
              icon: 'far:car-wrench',
            }}
            onClick={() => {
              setValue('faultType', FaultType.other, { shouldValidate: true, shouldDirty: true });
            }}
          />

          <FormHelperText className="error-block" error>
            {getErrorMessage(t, errors.faultType?.type)}
          </FormHelperText>
        </FormLayout>
      </FormControl>

      <FormLayout className="m-24px">
        <Heading4Text className="m-12px" weight={600}>
          {t('auth:unregistered.step-4.fault-information-title')}
        </Heading4Text>
        <ControlledInput
          inputProps={{
            rows: 2,
            multiline: true,
            variant: 'filled',
            placeholder: t('auth:unregistered.step-4.fault-information-placeholder'),
            inputProps: {
              maxLength: 255,
            },
            'data-cy': 'faultInformation',
          }}
          controllerProps={{
            control,
            name: 'faultInformation',
          }}
        />
      </FormLayout>
      {faultType === FaultType.other && !faultInformation?.trim() && (
        <FormHelperText className="m-24px" error>
          {getErrorMessage(t, 'required')}
        </FormHelperText>
      )}
    </UnregisteredStep5Style>
  );
};

export default Unregistered5;
